// React
import React, { ReactNode } from 'react'

// Next
import Image from 'next/image'
import Link from 'next/link'

// Components
import { Button } from '@/components/ui/button'
import LinkedText from './LinkedText'

// External
import { ArrowRightIcon } from 'lucide-react'

// Utils
import { LinkedContent } from '@/utils/ui/types/linked-content'

interface HeroCompProps {
  mainHeading: string
  subHeading: string
  text: string | LinkedContent
  imageSrc: string
}

const HeroComp: React.FC<HeroCompProps> = ({
  mainHeading,
  subHeading,
  text,
  imageSrc,
}) => {
  // Render the text content based on its type
  const renderText = (): ReactNode => {
    if (typeof text === 'string') {
      return text
    }

    if (text && typeof text === 'object') {
      // Check if it has part1, link1, etc. structure
      const keys = Object.keys(text)
      const hasStructuredLinks = keys.some((key) =>
        key.match(/^(part|link)\d+$/)
      )

      if (hasStructuredLinks) {
        return <LinkedText content={text} />
      }
    }

    // Fallback
    return String(text)
  }

  return (
    <div className='relative isolate overflow-hidden bg-white'>
      <div className='mx-auto max-w-7xl px-6 py-12 sm:py-16 lg:flex lg:items-center lg:gap-x-10 lg:px-8'>
        <div className='mx-auto max-w-2xl lg:mx-0 lg:flex-auto'>
          <h1 className='mt-10 max-w-lg text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl'>
            {mainHeading}
          </h1>
          <h2 className='mt-6 text-lg font-semibold text-indigo-600'>
            {subHeading}
          </h2>
          <p className='mt-6 text-lg leading-8 text-gray-600'>{renderText()}</p>
          <div className='mt-10 flex items-center gap-x-6'>
            <Button asChild size='lg'>
              <Link href='/text-to-meme'>Get started</Link>
            </Button>
            <Button asChild variant='outline' size='lg'>
              <Link href='/'>
                <span>Learn more</span>
                <ArrowRightIcon className='ml-2 h-4 w-4' />
              </Link>
            </Button>
          </div>
        </div>
        <div className='mt-16 sm:mt-24 lg:mt-0 lg:flex-shrink-0 lg:flex-grow'>
          <Image
            src={imageSrc}
            alt='Hero Image'
            width={500}
            height={500}
            className='mx-auto drop-shadow-xl'
          />
        </div>
      </div>
    </div>
  )
}

export default HeroComp
